import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { BrandMark } from '../components/BrandMark';
import { DownloadQrBlock } from '../components/DownloadQrBlock';
import { appConfig } from '../config/appConfig';
import { useAuth } from '../context/AuthContext';
import { markOnboardingDone } from '../utils/onboardingSession';

const FEATURES = [
  {
    index: '01',
    title: '随手收录素材',
    body: '把值得一读的内容第一时间存入日记，不再遗漏',
  },
  {
    index: '02',
    title: '颗粒化提炼',
    body: '提炼知识颗粒，沉淀可复用、可检索的个人理解',
  },
  {
    index: '03',
    title: '间隔重复回顾',
    body: '科学调度复习节奏，让学过的内容真正留下来',
  },
] as const;

export function OnboardingPage() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 120);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'apple-itunes-app';
    meta.content = 'app-id=6776661422';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  if (status === 'signed-in') {
    return <Navigate to="/" replace />;
  }

  function goDiaryLogin() {
    markOnboardingDone();
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-[#0C1220] text-[#F0F5FF]">
      <div
        className={`mx-auto flex min-h-screen max-w-5xl flex-col px-5 pb-10 pt-10 transition-all duration-700 sm:px-8 sm:pt-14 lg:px-10 ${
          visible ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
        }`}
      >
        <header className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandMark size={40} />
            <p className="text-sm font-semibold tracking-wide text-[#F0F5FF]">{appConfig.appName}</p>
          </div>
          <button
            type="button"
            onClick={goDiaryLogin}
            className="rounded-full border border-white/15 px-4 py-2 text-sm text-[#D5E4F5] transition hover:bg-white/10"
          >
            登录
          </button>
        </header>

        <section className="flex flex-1 flex-col justify-center py-10 lg:py-16">
          <div className="space-y-5">
            <div className="flex items-start justify-between gap-4 sm:gap-6">
              <h1 className="min-w-0 flex-1 text-[2rem] font-extrabold leading-[1.2] tracking-[-0.03em] sm:text-5xl sm:leading-[1.15]">
                学而时习
                <span className="mt-1 block text-[#9CC7F0]">知方得存</span>
              </h1>
              <BrandMark size={168} className="size-[88px] shrink-0 sm:size-[168px]" />
            </div>
            <p className="max-w-md text-base leading-7 text-[#A8B8CC] sm:text-lg">
              私人学习素材库与知识颗粒系统。用浏览器开始，或下载手机 App。
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={goDiaryLogin}
                className="w-full rounded-full bg-[#0F6CBD] px-6 py-3.5 text-base font-bold text-white transition hover:opacity-90 active:opacity-80 sm:w-auto"
              >
                开启颗粒日记
              </button>
              <a
                href="/download"
                className="w-full rounded-full border border-white/20 px-6 py-3.5 text-center text-base font-semibold text-[#D5E4F5] transition hover:bg-white/10 sm:w-auto"
              >
                下载 App
              </a>
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-3 sm:gap-4">
          {FEATURES.map((feature, index) => (
            <article
              key={feature.index}
              className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition-all duration-500"
              style={{
                transitionDelay: `${180 + index * 140}ms`,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(28px)',
              }}
            >
              <span className="mt-0.5 min-w-[20px] text-xs font-bold tracking-wide text-[#5BA8E8]">
                {feature.index}
              </span>
              <div className="space-y-1">
                <h2 className="text-sm font-semibold text-[#F0F5FF]">{feature.title}</h2>
                <p className="text-sm leading-5 text-[#8A9CB8]">{feature.body}</p>
              </div>
            </article>
          ))}
        </section>

        <div className="mt-8 sm:mt-10">
          <DownloadQrBlock tone="dark" />
        </div>

        <p className="mt-8 text-center text-xs text-[#6D7C93]">
          © {new Date().getFullYear()} {appConfig.appName}
          {' · '}
          <a href="https://beian.miit.gov.cn/" className="hover:text-[#A8B8CC]" target="_blank" rel="noreferrer">
            {appConfig.icpWebsite}
          </a>
        </p>
      </div>
    </div>
  );
}
